import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(duration);

export const useCalendarStore = defineStore('calendar', {
    state: () => ({
        errors: [],
        loading: false,
        showViewDropdown: false,
        startDate: '',
        endDate: '',
        checkIn: '',
        checkOut: '',
        baseUrl: '',
        url: '',
        preloadUrl: '',
        slug: '',
        events: [],
        preloadedEvents: [],
        resources: [],
        weekdays: [
            { full: 'Monday', short: 'Mon', shorter: 'Mo', shortest: 'M' },
            { full: 'Tuesday', short: 'Tue', shorter: 'Tu', shortest: 'T' },
            { full: 'Wednesday', short: 'Wed', shorter: 'We', shortest: 'W' },
            { full: 'Thursday', short: 'Thu', shorter: 'Th', shortest: 'T' },
            { full: 'Friday', short: 'Fri', shorter: 'Fr', shortest: 'F' },
            { full: 'Saturday', short: 'Sat', shorter: 'Sa', shortest: 'S' },
            { full: 'Sunday', short: 'Sun', shorter: 'Su', shortest: 'S' },
        ],
        months: [
            { id: 1, label: 'January', short: 'Jan' },
            { id: 2, label: 'February', short: 'Feb' },
            { id: 3, label: 'March', short: 'Mar' },
            { id: 4, label: 'April', short: 'Apr' },
            { id: 5, label: 'May', short: 'May' },
            { id: 6, label: 'June', short: 'Jun' },
            { id: 7, label: 'July', short: 'Jul' },
            { id: 8, label: 'August', short: 'Aug' },
            { id: 9, label: 'September', short: 'Sep' },
            { id: 10, label: 'October', short: 'Oct' },
            { id: 11, label: 'November', short: 'Nov' },
            { id: 12, label: 'December', short: 'Dec' },
        ],
        view: 'month',
        activeView: { label: 'Month', name: 'month' },
        views: [
            { label: 'List', name: 'list', hidden: false },
            { label: 'Day', name: 'day', hidden: false },
            { label: 'Month', name: 'month', hidden: false },
            { label: 'Year', name: 'year', hidden: false },
        ],
        desktopViews: [
            { label: 'List', name: 'list', hidden: false },
            { label: 'Day', name: 'day', hidden: false },
            { label: 'Month', name: 'month', hidden: false },
            { label: 'Year', name: 'year', hidden: false },
        ],
        mobileViews: [
            { label: 'List', name: 'list', hidden: false },
            { label: 'Day', name: 'day', hidden: false },
            { label: 'Month', name: 'month', hidden: false },
        ],
        activeDate: dayjs(),
        chosenDate: dayjs(),
        today: dayjs(),
        monthDate: null,
        preloadedYear: false,
        last_updated: null,
        activeTab: 'list',
    }),
    getters: {
        currentView() {
            return this.views.find((item) => {
                return item.name === this.view;
            });
        },
        activeDateFormatted() {
            return this.activeDate.format('YYYY-MM-DD');
        },
        chosenDateFormatted() {
            return this.chosenDate.format('YYYY-MM-DD');
        },
        todayFormatted() {
            return this.today.format('YYYY-MM-DD');
        },
        endOfMonthFormatted() {
            return this.today.endOf('month').format('YYYY-MM-DD');
        },
        yearMonthRange() {
            return {
                startDate: dayjs(this.activeDate)
                    .startOf('year')
                    .format('YYYY-MM-DD'),
                endDate: dayjs(this.activeDate)
                    .endOf('year')
                    .format('YYYY-MM-DD'),
            };
        },
        monthsInActiveYear() {
            let months = [];
            for (var i = 0; i < 12; i++) {
                let d = dayjs(`${this.activeYear.full}-${i + 1}-01`).format(
                    'YYYY-MM-DD'
                );
                months.push(d);
            }
            return months;
        },
        activeMonth() {
            return {
                full: this.activeDate.format('MMMM'),
                short: this.activeDate.format('MMM'),
                num: this.activeDate.format('MM'),
            };
        },
        activeDay() {
            return {
                full: this.activeDate.format('dddd'),
                short: this.activeDate.format('ddd'),
                num: this.activeDate.format('DD'),
            };
        },
        activeYear() {
            return {
                full: this.activeDate.format('YYYY'),
                short: this.activeDate.format('YY'),
            };
        },
        dayEvents() {
            return this.events.filter((event) => {
                const isSame = dayjs(this.chosenDate).isSame(
                    event.start_date,
                    'date'
                );
                return isSame ? true : false;
            });
        },
        queryStartDate() {
            if (this.view === 'month' || this.view === 'list') {
                return this.activeDate.startOf('month').format('YYYY-MM-DD');
            }
            if (this.view === 'year') {
                return this.activeDate.startOf('year').format('YYYY-MM-DD');
            }
        },
        queryEndDate() {
            if (this.view === 'month' || this.view === 'list') {
                return this.activeDate.endOf('month').format('YYYY-MM-DD');
            }
            if (this.view === 'year') {
                return this.activeDate.endOf('year').format('YYYY-MM-DD');
            }
        },
        currentViewLabel() {
            let d = '';
            switch (this.view) {
                case 'year':
                    d = this.activeYear.full;
                    break;
                case 'month':
                    d = this.activeMonth.full + ' ' + this.activeYear.full;
                    break;
                case 'list':
                    d = this.activeMonth.full + ' ' + this.activeYear.full;
                    break;
                case 'day':
                    d =
                        this.activeMonth.full +
                        ' ' +
                        this.activeDay.num +
                        ', ' +
                        this.activeYear.full;
                    break;
            }
            return d;
        },
        nights(state) {
            if (!state.startDate) {
                return 1;
            }
            let diff = dayjs(state.endDate).diff(dayjs(state.startDate), 'd');
            return diff < 2 ? 1 : diff;
        },
    },
    actions: {
        setSlug(slug) {
            this.slug = slug;
        },
        setStartDate(val) {
            this.startDate = val;
        },
        setEndDate(val) {
            this.endDate = val;
        },
        setDateRange(dateRange) {
            this.startDate = dateRange.startDate;
            this.endDate = dateRange.endDate;

            this.checkIn = dateRange.startDate;
            this.checkOut = dateRange.endDate;

            this.activeDate = this.startDate ? dayjs(this.startDate) : dayjs();
            this.chosenDate = this.activeDate;
        },
        setCheckInRange(dateRange) {
            this.checkIn = dateRange.checkIn;
            this.checkOut = dateRange.checkOut;
        },
        setCheckIn(val) {
            this.checkIn = val;
        },
        setCheckOut(val) {
            this.checkOut = val;
        },
        setUrl(url) {
            this.baseUrl = url;
        },
        setQuery() {
            if (this.startDate && this.endDate) {
                this.url =
                    this.baseUrl +
                    `?start_date=${dayjs(this.startDate).format('YYYY-MM-DD')}&end_date=${dayjs(this.endDate).format('YYYY-MM-DD')}&tab=${this.activeTab}`;
                return;
            }

            if (this.queryStartDate && this.queryEndDate) {
                this.url =
                    this.baseUrl +
                    `?start_date=${this.queryStartDate}&end_date=${this.queryEndDate}&tab=${this.activeTab}`;
                return;
            }
        },
        setPreloadQuery() {
            const startDate = this.activeDate
                .startOf('year')
                .format('YYYY-MM-DD');
            const endDate = this.activeDate.endOf('year').format('YYYY-MM-DD');

            this.preloadUrl =
                this.baseUrl + `?start_date=${startDate}&end_date=${endDate}`;
        },
        getEvents() {
            this.setQuery();
            this.loading = true;

            return ApiService.apiCall(this.url, 'get')
                .then((response) => {
                    this.events = response.data.events;
                    this.last_updated = response.data.last_property_scrape;
                    this.loading = false;
                    return this.events;
                })
                .catch((response) => {
                    this.loading = false;
                });
        },
        preLoadEvents() {
            this.setPreloadQuery();
            return ApiService.apiCall(this.preloadUrl, 'get')
                .then((response) => {
                    this.preloadedYear = true;
                    this.preloadedEvents = response.data.events;
                    return this.preloadedEvents;
                })
                .catch((response) => {
                });
        },
        setView(view) {
            let preloaded = false;
            if (view.name === 'year' && this.preloadedYear) {
                this.events = this.preloadedEvents;
                preloaded = true;
            }

            if (view.name === 'month') {
                this.chosenDate = this.activeDate;
            }

            this.activeView = view;
            this.view = view.name;
            this.showViewDropdown = false;

            this.setViewDateRange();

            if (!preloaded) {
                this.getEvents();
            }
        },
        setViewDropdown(type) {
            if (type === 'mobile') {
                this.views = this.mobileViews;
            } else {
                this.views = this.desktopViews;
            }
        },
        setViewDateRange() {
            const view = this.view;

            switch (view) {
                case 'year':
                    this.startDate = dayjs(this.activeDate)
                        .startOf('year')
                        .format('YYYY-MM-DD');
                    this.endDate = dayjs(this.activeDate)
                        .endOf('year')
                        .format('YYYY-MM-DD');
                    break;

                case 'month':
                case 'list':
                case 'day':
                    this.startDate = dayjs(this.activeDate)
                        .startOf('month')
                        .format('YYYY-MM-DD');
                    this.endDate = dayjs(this.activeDate)
                        .endOf('month')
                        .format('YYYY-MM-DD');
                    break;
            }
        },
        setPreloadedYear(val) {
            this.preloadedYear = val;
        },
        setToday() {
            this.today = dayjs();
            this.activeDate = dayjs();
            this.chosenDate = dayjs();
        },
        setChosenDate(date) {
            this.chosenDate = dayjs(date);
            if (this.view === 'day') {
                this.activeDate = this.chosenDate;
            }
        },
        nextDay() {
            this.activeDate = this.activeDate.add(1, 'day');
            this.chosenDate = this.activeDate;
        },
        prevDay() {
            this.activeDate = this.activeDate.subtract(1, 'day');
            this.chosenDate = this.activeDate;
        },
        nextWeek() {
            this.activeDate = this.activeDate.add(1, 'week');
        },
        prevWeek() {
            this.activeDate = this.activeDate.subtract(1, 'week');
        },
        nextMonth() {
            this.activeDate = this.activeDate.add(1, 'month').startOf('month');
            this.chosenDate = this.activeDate;
            this.startDate = this.activeDate.startOf('month');
            this.endDate = this.activeDate.endOf('month');
            this.getEvents();
        },
        prevMonth() {
            this.activeDate = this.activeDate
                .subtract(1, 'month')
                .startOf('month');
            this.chosenDate = this.activeDate;
            this.startDate = this.activeDate.startOf('month');
            this.endDate = this.activeDate.endOf('month');
            this.getEvents();
        },
        nextYear() {
            this.activeDate = this.activeDate.add(1, 'year');
        },
        prevYear() {
            this.activeDate = this.activeDate.subtract(1, 'year');
        },
        setLoading(val) {
            this.loading = val;
        },
        setShowViewDropdown(val) {
            this.showViewDropdown = val;
        },
        getWeekday(date) {
            return dayjs(date).weekday();
        },
        setMonthDate(val) {
            this.monthDate = dayjs(val);
        },
        setTab(tab) {
            this.activeTab = tab;

            if (tab === 'calendar') {
                this.startDate = Date(dayjs().startOf('month'));
                this.endDate = Date(dayjs().endOf('month'));
                this.getEvents();
            }
            if (tab === 'list') {
                if (!this.checkIn || !this.checkOut) {
                    this.events = [];
                    return;
                }

                this.startDate = this.checkIn;
                this.endDate = this.checkOut;

                this.getEvents();
            }
        },
        gotoAllAvailability() {
            this.setTab('calendar');
            this.setView({ name: 'year', label: 'Year' });
        },
    },
});
